import { Card, CardContent, Typography } from "@mui/material";
import { CellContext, ColumnDef, SortingState } from "@tanstack/react-table";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Unauthorized } from "../../components";
import { DataTable } from "../../components/data-table/DataTable";
import { PageLayout } from "../../components/PageLayout";
import { useAllUserGroupsForACompany } from "../../hooks/useAllUserGroupsForACompany";
import { useCheckAreaAccess } from "../../hooks/useCheckAreaAccess";
import { useCompanyData } from "../../hooks/useCompanyData";
import { useCompanyDefaultUserGroups } from "../../hooks/useCompanyDefaultUserGroups";
import { useLocalization } from "../../hooks/useLocalization";
import { Routes } from "../../types/NavigationPaths";
import { AddUserGroupDialog } from "./AddUserGroupDialog";

export interface UserGroupRow {
  userGroupId: string;
  userGroupName: string;
  userCount: number;
  experienceCount: number;
  isPrimaryGroup: boolean;
  isDefaultGroup: boolean;
}

interface GroupNameCellProps {
  ctx: CellContext<UserGroupRow, any>;
  isDefaultGroup?: boolean;
  isPrimaryGroup?: boolean;
}

export const GroupNameCell: React.FC<GroupNameCellProps> = ({
  ctx,
  isPrimaryGroup,
}) => {
  const groupName = ctx.getValue<string>();

  return (
    <span style={{ fontWeight: isPrimaryGroup ? 700 : "inherit" }}>
      {groupName}
    </span>
  );
};

const INITIAL_SORTING: SortingState = [{ id: "userGroupName", desc: false }];

export const GroupsPage: React.FC = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { companyId } = useParams();
  const { isLoading: isCheckAccessLoading, access } = useCheckAreaAccess(companyId);
  const areHooksEnabled = !!companyId && !isCheckAccessLoading && access.uam;
  const {
    groups,
    isLoading: isGroupsLoading,
    error,
  } = useAllUserGroupsForACompany(companyId, areHooksEnabled);

  const { isLoading: isCompanyLoading } = useCompanyData(companyId, areHooksEnabled);

  // Use the custom hook to get default user groups for the company
  const { defaultUserGroups, isLoading: isCompanyDefaultGroupsLoading } = useCompanyDefaultUserGroups(companyId, areHooksEnabled);

  const formattedGroups = useMemo(() => {
    return groups.map<UserGroupRow>((group) => {
      // Find if this group exists in the list of default groups from the hook
      const defaultGroup = defaultUserGroups.find(
        (def) => def.userGroupId === group.userGroupId
      );

      return {
        userGroupId: group.userGroupId,
        userGroupName: group.userGroupName,
        userCount: group.userCount,
        experienceCount: group.experienceCount,
        isDefaultGroup: defaultGroup !== undefined,
        isPrimaryGroup: !!defaultGroup?.isPrimaryGroup,
      };
    });
  }, [groups, defaultUserGroups]);

  const handleCreateGroup = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleRowClick = useCallback(
    (userGroup: UserGroupRow) => {
      void navigate(`/companies/${companyId}${Routes.GROUPS}/${userGroup.userGroupId}`);
    },
    [navigate],
  );

  const renderGroupNameCell = useCallback(
    (ctx: CellContext<UserGroupRow, any>) => {
      return (
        <GroupNameCell ctx={ctx} isDefaultGroup={ctx.row.original.isDefaultGroup} isPrimaryGroup={ctx.row.original.isPrimaryGroup} />
      );
    },
    [],
  );

  const columns = useMemo<ColumnDef<UserGroupRow, any>[]>(
    () => [
      {
        id: "userGroupName",
        header: t("groups.groupsTableHeaders.name"),
        accessorKey: "userGroupName",
        cell: renderGroupNameCell,
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "text",
      },
      {
        id: "defaultGroup",
        header: "Default Group",
        accessorKey: "isDefaultGroup",
        cell: (ctx) => ctx.getValue<boolean>() ? t("yes") : t("no"),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "userCount",
        header: t("groups.groupsTableHeaders.users"),
        cell: (ctx) => ctx.getValue<string>(),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "experienceCount",
        header: t("groups.groupsTableHeaders.experiences"),
        cell: (ctx) => ctx.getValue<string>(),
        enableSorting: true,
        enableColumnFilter: false,
      },
    ],
    [renderGroupNameCell, t],
  );

  const loading = isGroupsLoading || isCompanyLoading || isCompanyDefaultGroupsLoading;

  if (error) {
    return (
      <PageLayout isLoading={loading}>
        <Card>
          <CardContent>
            <Typography color="error" variant="body1">
              {t("groups.errorLoadingGroups", { message: error.message })}
            </Typography>
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  if (!loading && !access.uam) {
    return (<Unauthorized />)
  }

  return (
    <PageLayout isLoading={loading} maxWidth={false}>
      <DataTable<UserGroupRow>
        actionButtonText={t("groups.createGroup")}
        actionButtonTextShort={t("groups.create")}
        columns={columns}
        data={formattedGroups}
        defaultSort={INITIAL_SORTING}
        globalFilterPlaceholder={t("groups.searchGroups")}
        onActionClick={handleCreateGroup}
        onRowClick={handleRowClick}
        showActionButton
      />
      <AddUserGroupDialog
        companyId={companyId}
        onClose={handleCloseDialog}
        open={isDialogOpen}
      />
    </PageLayout>
  );
};
